// Web → Analytika → Živě.
//
// Kdo je na fitway40.cz právě teď. Data z /api/web-live, obnovuje se samo
// každých 15 sekund. Když se okno adminu schová na pozadí, obnovování se
// zastaví a naskočí zpátky, jakmile se na kartu vrátíte — jinak by to
// zbytečně bušilo do databáze celý den.
//
// „Právě teď" znamená návštěvnici, od které přišla nějaká událost za
// posledních 5 minut. Kdo si stránku otevře a nic nedělá, po pěti minutách
// ze seznamu zmizí, i když u ní pořád sedí. Přesněji to bez cookies nejde.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Radio, Users, Eye, Clock } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { fetchApi } from "../../lib/api.js";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import { Dlazdice, ErrorNote } from "../../components/ui.jsx";
import { KartaPrehledu, HlavickaDlazdice, Pilulka } from "../../components/PrehledDlazdice.jsx";
import InfoTip from "../../components/InfoTip.jsx";

const BRAND = "#6E519F";
const GRID = "#EAE6EF";

const OBNOVA_MS = 15000;

// Hezčí jméno stránky, ať se v tabulce nekouká na holé adresy.
const NAZVY = {
  "/": "Domovská stránka",
  "/kviz/": "Kvíz",
  "/kviz/dekuji/": "Kvíz → potvrďte e-mail",
  "/kviz/potvrzeno/": "Kvíz → potvrzeno",
  "/kalkulator/": "Kalkulátor",
  "/jsz/": "Prodejka (plná cena)",
  "/jsz-sleva/": "Prodejka (tajná sleva)",
  "/jsz-nakup/": "Objednávka",
  "/jsz-sleva-nakup/": "Objednávka (sleva)",
  "/dekujeme-za-nakup/": "Po nákupu",
};
const nazev = (p) => NAZVY[p] || p || "neznámo";

const TYPY = {
  view: "otevřela stránku",
  click: "klikla k objednávce",
  purchase: "zaplatila",
  krok: "krok",
  dotaznik: "odeslala dotazník",
};

const doba = (s) => {
  const n = Math.max(0, Number(s) || 0);
  if (n < 60) return `${n} s`;
  if (n < 3600) return `${Math.floor(n / 60)} m`;
  return `${Math.floor(n / 3600)} h ${Math.floor((n % 3600) / 60)} m`;
};

export default function Zive() {
  const [data, setData] = useState(null);
  const [chyba, setChyba] = useState(null);
  const [tik, setTik] = useState(false); // krátké bliknutí tečky po načtení
  const uzka = useUzkaObrazovka();
  const bezi = useRef(true);

  const nacti = useCallback(async () => {
    try {
      const j = await fetchApi("/api/web-live");
      if (!bezi.current) return;
      setData(j);
      setChyba(null);
      setTik(true);
      setTimeout(() => bezi.current && setTik(false), 700);
    } catch (e) {
      if (bezi.current) setChyba(e.message);
    }
  }, []);

  useEffect(() => {
    bezi.current = true;
    nacti();
    const id = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      nacti();
    }, OBNOVA_MS);
    const naVratceNaKartu = () => {
      if (typeof document !== "undefined" && !document.hidden) nacti();
    };
    document.addEventListener("visibilitychange", naVratceNaKartu);
    return () => {
      bezi.current = false;
      clearInterval(id);
      document.removeEventListener("visibilitychange", naVratceNaKartu);
    };
  }, [nacti]);

  const s = data?.souhrn || {};
  const lide = data?.lide || [];
  const stranky = data?.stranky || [];
  const zdroje = data?.zdroje || [];
  const minuty = data?.minuty || [];
  const udalosti = data?.udalosti || [];

  const potvrzeniPct = s.odeslano > 0 ? Math.round((s.potvrzeno / s.odeslano) * 1000) / 10 : 0;

  return (
    <div className="space-y-5">
      {chyba && <ErrorNote>Živá data se nepodařilo načíst: {chyba}</ErrorNote>}

      {/* ------------------------------------------------------ hlavní číslo */}
      <KartaPrehledu>
        <div className="flex flex-wrap items-center justify-between gap-4 p-4 sm:p-5">
          <div className="flex items-center gap-4">
            <span className="relative flex h-3.5 w-3.5">
              <span
                className={`absolute inline-flex h-full w-full rounded-full bg-emerald-400 ${
                  s.ted > 0 ? "animate-ping opacity-75" : "opacity-0"
                }`}
              />
              <span
                className={`relative inline-flex h-3.5 w-3.5 rounded-full ${
                  s.ted > 0 ? "bg-emerald-500" : "bg-slate-300"
                }`}
              />
            </span>
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-semibold tabular-nums text-slate-900">{s.ted ?? 0}</span>
                <span className="text-base text-slate-600">
                  {s.ted === 1 ? "návštěvnice právě teď" : "návštěvnic právě teď"}
                </span>
                <InfoTip text="Kdo za posledních 5 minut něco udělal: otevřel stránku, scrolloval, klikl. Kdo si stránku otevře a pak jen čte bez pohybu, po pěti minutách z čísla vypadne." />
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                <span>
                  Za poslední hodinu <strong className="tabular-nums text-slate-700">{s.hodina ?? 0}</strong>
                </span>
                <span>obnovuje se každých 15 s</span>
              </div>
            </div>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition ${
              tik ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
            }`}
          >
            <Radio className="h-4 w-4" /> Živě
          </span>
        </div>
      </KartaPrehledu>

      {/* --------------------------------------------------------- dnešek */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Dlazdice ton="znacka" popisek="Dnes návštěvnice" hodnota={s.dnes ?? 0} />
        <Dlazdice ton="info" popisek="Dnes zobrazení" hodnota={s.zobrazeni ?? 0} />
        <Dlazdice
          ton="neutralni"
          popisek={<>Dnes kvíz<InfoTip text="Kolik různých žen dnes otevřelo stránku s kvízem." /></>}
          hodnota={s.kviz ?? 0}
        />
        <Dlazdice
          ton="zlata"
          popisek={<>Odesláno<InfoTip text="Došly na /kviz/dekuji/, tedy formulář opravdu odeslaly. Kontakt ale ještě není v seznamu, dokud e-mail nepotvrdí." /></>}
          hodnota={s.odeslano ?? 0}
        />
        <Dlazdice
          ton={potvrzeniPct >= 60 ? "ok" : "pozor"}
          popisek={<>Potvrdilo<InfoTip text="Klikly v e-mailu na potvrzení a došly na /kviz/potvrzeno/. Teprve tyhle ženy jsou hotové kontakty, kterým odejde série." /></>}
          hodnota={s.potvrzeno ?? 0}
          doplnky={s.odeslano > 0 ? <Pilulka>{potvrzeniPct} % z odeslaných</Pilulka> : null}
        />
        <Dlazdice ton="ok" popisek="Dnes nákupy" hodnota={s.nakupy ?? 0} />
      </div>

      {/* ------------------------------------------------ posledních 30 minut */}
      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={Clock}
          nazev="Posledních 30 minut"
          napoveda="Kolik různých návštěvnic bylo na webu v každé minutě."
        />
        <div className="p-3 sm:p-4">
        <ResponsiveContainer width="100%" height={160}>
          <AreaChart data={minuty} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
            <XAxis dataKey="cas" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={24} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={28} />
            <Tooltip formatter={(v) => [v, "Návštěvnice"]} labelStyle={{ fontWeight: 600 }} />
            <Area type="monotone" dataKey="lidi" stroke={BRAND} fill={BRAND} fillOpacity={0.22} strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
        </div>
      </KartaPrehledu>

      {/* ------------------------------------------------------ kde právě jsou */}
      <div className="grid gap-4 lg:grid-cols-2">
        <KartaPrehledu>
          <HlavickaDlazdice
            ikona={Eye}
            nazev="Na které stránce jsou"
            napoveda="Poslední stránka, na které se návštěvnice ozvala."
          />
          <ul className="space-y-2 p-3 sm:p-4">
            {stranky.map((p) => (
              <li key={p.path} className="flex items-center gap-3 text-sm">
                <span className="truncate text-slate-800">{nazev(p.path)}</span>
                <span className="ml-auto shrink-0 tabular-nums font-semibold text-slate-900">{p.lidi}</span>
                <span className="h-2 w-16 shrink-0 overflow-hidden rounded-full bg-slate-100">
                  <span
                    className="block h-full rounded-full"
                    style={{
                      width: `${Math.round((p.lidi / Math.max(1, stranky[0]?.lidi || 1)) * 100)}%`,
                      backgroundColor: BRAND,
                    }}
                  />
                </span>
              </li>
            ))}
            {!stranky.length && <li className="py-4 text-center text-sm text-slate-400">Právě teď je web prázdný</li>}
          </ul>
        </KartaPrehledu>

        <KartaPrehledu>
          <HlavickaDlazdice
            ikona={Users}
            nazev="Odkud přišly"
            napoveda="Zdroj podle první stránky, kterou dnes otevřely. Ne podle té, na které stojí teď."
          />
          <ul className="space-y-2 p-3 sm:p-4">
            {zdroje.map((z) => (
              <li key={z.zdroj} className="flex items-center gap-3 text-sm">
                <span className="truncate text-slate-800">{z.zdroj}</span>
                <span className="ml-auto shrink-0 tabular-nums font-semibold text-slate-900">{z.lidi}</span>
              </li>
            ))}
            {!zdroje.length && <li className="py-4 text-center text-sm text-slate-400">Zatím nikdo</li>}
          </ul>
        </KartaPrehledu>
      </div>

      {/* --------------------------------------------------- kdo je tu teď */}
      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={Radio}
          nazev="Kdo je právě na webu"
          napoveda="Jeden řádek je jedna žena. Bez jmen a bez IP adres, rozlišujeme je jen anonymním otiskem, který se každý den mění. Ticho říká, jak dlouho se neozvala."
        />
        {/* 13.9.2026 (fáze 4i): na telefonu seznam řádků. Šestisloupcová tabulka
            měla min-w 560 px, takže se s ní na 390 px muselo rolovat do stran. */}
        {uzka ? (
          <div className="divide-y divide-slate-100">
            {lide.map((l, i) => (
              <div key={i} className="px-4 py-3">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="min-w-0 truncate text-sm font-semibold text-slate-800">{nazev(l.path)}</span>
                  <span className={`shrink-0 whitespace-nowrap text-xs font-semibold tabular-nums ${l.ticho_s < 60 ? "text-emerald-600" : "text-slate-500"}`}>
                    ticho {doba(l.ticho_s)}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <Pilulka>{l.zdroj}</Pilulka>
                  <Pilulka>{l.zarizeni}</Pilulka>
                  <Pilulka>{l.stranek} stránek</Pilulka>
                  <Pilulka>na webu {doba(l.na_webu_s)}</Pilulka>
                </div>
              </div>
            ))}
            {!lide.length && <div className="py-6 text-center text-slate-400">Právě teď na webu nikdo není</div>}
          </div>
        ) : (
        <div className="overflow-x-auto p-3 sm:p-4">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500">
                <th className="py-2 font-medium">Kde je</th>
                <th className="py-2 font-medium">Odkud přišla</th>
                <th className="py-2 font-medium">Zařízení</th>
                <th className="py-2 text-right font-medium">Stránek</th>
                <th className="py-2 text-right font-medium">Na webu</th>
                <th className="py-2 text-right font-medium">Ticho</th>
              </tr>
            </thead>
            <tbody>
              {lide.map((l, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 pr-2 text-slate-800">{nazev(l.path)}</td>
                  <td className="py-2 pr-2 text-slate-600">{l.zdroj}</td>
                  <td className="py-2 pr-2 text-slate-600">{l.zarizeni}</td>
                  <td className="py-2 text-right tabular-nums text-slate-700">{l.stranek}</td>
                  <td className="py-2 text-right tabular-nums text-slate-700">{doba(l.na_webu_s)}</td>
                  <td
                    className={`py-2 text-right tabular-nums ${
                      l.ticho_s < 60 ? "font-medium text-emerald-600" : "text-slate-500"
                    }`}
                  >
                    {doba(l.ticho_s)}
                  </td>
                </tr>
              ))}
              {!lide.length && (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-slate-400">
                    Právě teď na webu nikdo není
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </KartaPrehledu>

      {/* ------------------------------------------------- poslední události */}
      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={Radio}
          nazev="Co se právě děje"
          napoveda="Poslední události za hodinu, nejnovější nahoře. Otevření stránky, kliknutí k objednávce, kroky kvízu a nákupy."
        />
        <ul className="divide-y divide-slate-100 px-3 sm:px-4">
          {udalosti.map((u, i) => (
            <li key={i} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
              <span className="w-16 shrink-0 tabular-nums text-slate-400">{u.cas}</span>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                  u.type === "purchase"
                    ? "bg-emerald-100 text-emerald-700"
                    : u.type === "click"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                {TYPY[u.type] || u.type}
              </span>
              <span className="text-slate-800">{nazev(u.path)}</span>
              {u.type === "krok" && u.krok != null && (
                <span className="inline-flex items-center gap-2 text-slate-500">
                  <span>
                    krok {u.krok}/{u.celkem}
                  </span>
                  {u.section && <span>{u.section}</span>}
                </span>
              )}
              {u.type === "purchase" && u.value != null && (
                <span className="font-medium text-emerald-700">{Math.round(u.value)} Kč</span>
              )}
              <span className="ml-auto inline-flex shrink-0 items-center gap-2 text-xs text-slate-400">
                <span>{u.zdroj}</span>
                <span>{u.zarizeni}</span>
              </span>
            </li>
          ))}
          {!udalosti.length && <li className="py-6 text-center text-slate-400">Za poslední hodinu se nic nestalo</li>}
        </ul>
      </KartaPrehledu>

      <p className="px-1 pb-2 text-xs leading-relaxed text-slate-500">
        Živý přehled nic nového neukládá. Nevidíte jména, e-maily ani IP adresy, jen anonymní otisk, který se
        každý den mění. Proto tu nejde nikoho konkrétního sledovat a měření běží i bez souhlasu s cookies.
      </p>
    </div>
  );
}
